import React, {useContext, useEffect, useState} from 'react';
import {Pressable} from 'react-native';
import {Email, Name, Phone} from './SubSteps';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import MainStyle from "../../../../../../MainStyle";
import Button from "../../../../../components/Button";
import {StoreContext} from "../../../../../components/StoreContextProvider";
import {dateFormat, isFullSwing, phoneUtil, smgEnvironment} from "../../../../../constants";
import {profanity} from '@2toad/profanity';
import {LATIN_KEYBOARD_MESSAGE, LATIN_KEYBOARD_REGEX} from "../../../../../utils/inputValidation";
import ServiceClient from "../../../../../utils/ServiceClient";
import OtpModal from "../../../../../modals/OtpModal";
import {useNavigation, useRoute} from "@react-navigation/native";
import {registerUserPayloadType, updateUserPayloadType} from "../../../../../types";
import {MaterialIcons} from "@expo/vector-icons";
import P from "../../../../../lib/P";
import Div from "../../../../../lib/Div";
import {colors} from "../../../../../assets/styles/colors";
import {Moment} from "moment";
import Btn from "../../../../../components/Btn";
import {Gender} from "../../../../../../generated/smg_service_pb";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";

const initialValues: registerUserPayloadType = {
  email: '', smsCode: '', phone: '', username: '', handedness: '', handicap: 'N/A', dateOfBirth: null, firstname: '',
  lastname: '', preferredOtpMethod: 1, gender: Gender.UNSPECIFIED
};

type Props = { goNext: () => void, onCancel: () => void };

const ContactInfo = (props: Props) => {
  const isTestEnv = smgEnvironment === 'test';
  const [contactInfoStep, setContactInfoStep] = useState(isFullSwing ? 4 : 1);
  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      email: Yup.string().email().when([], {
        is: () => !isFullSwing,
        then: schema => schema.required(),
        otherwise: schema => schema.notRequired(),
      }),
      smsCode: Yup.string(), phone: Yup.string(), handicap: Yup.string(),
      username: Yup.string().required('Player Name is a required field').max(12, 'Screen Name must be 12 characters or less.')
        .matches(LATIN_KEYBOARD_REGEX, LATIN_KEYBOARD_MESSAGE),
      dateOfBirth: Yup.mixed<Moment>().nullable()
        .required('A valid date of birth is required.')
        .test('age-min', 'You must be at least 18 years old.', (value) => {
          if (!value) return false;
          const age = new Date().getFullYear() - value.year();
          return age >= 18;
        })
        .test('age-max', 'Please enter a valid date of birth.', (value) => {
          if (!value) return false;
          const age = new Date().getFullYear() - value.year();
          return age <= 100;
        }),
      handedness: Yup.string().when([], {
        is: () => !isFullSwing,
        then: schema => schema.required('Handedness is a required field'),
        otherwise: schema => schema.notRequired(),
      }),
      firstname: Yup.string().matches(LATIN_KEYBOARD_REGEX, LATIN_KEYBOARD_MESSAGE).when([], {
        is: () => !isFullSwing,
        then: schema => schema.required(),
        otherwise: schema => schema.notRequired(),
      }),
      lastname: Yup.string().matches(LATIN_KEYBOARD_REGEX, LATIN_KEYBOARD_MESSAGE).when([], {
        is: () => !isFullSwing,
        then: schema => schema.required(),
        otherwise: schema => schema.notRequired(),
      }),
      preferredOtpMethod: Yup.number(),
      gender: Yup.mixed<Gender>().test('fs-gender-required', 'This field is required.', (val) => !isFullSwing || (val !== undefined && val !== null && val !== Gender.UNSPECIFIED))
    }),
    onSubmit: (values) => {
      if (contactInfoStep === 4) {
        if (profanity.exists(values.username)) {
          formik.setFieldError('username', 'The selected user name is not allowed. Please choose another username');

        } else if (isFullSwing) {
          updateUser();

        } else {
          RegisterUser();
        }
      }
    },
    validate(values) {
      if (isTestEnv && !values.handedness.length) {
        return {handedness: 'Handedness is required'};
      }
    }
  });
  const Context = useContext(StoreContext);
  const {cancelModalConfig, user} = Context;
  const [phoneVerificationDialog, setPhoneVerificationDialog] = useState(false);
  const route = useRoute();
  const navigation: NativeStackNavigationProp<any> = useNavigation();

  // Post-registration finalizer. BSA cutover note: this used to call
  // partner-service.FullSwingCheckIn — see PartnerAuth.tsx
  // proceedAfterLogin comment. FSG-8881: the session join now happens
  // AFTER the full registration completes (profile here, then T&C) —
  // FullSwingAuth defers its implicit join while tnc_accepted=false, and
  // SignUp's GetUser() runs Context.completeRegistrationJoin() once the
  // T&C step finishes. goNext() still fires; nothing joins from here.
  const finishRegistration = () => {
    Context.setLoadingOverlay(false);
    props.goNext();
  };

  const updateUser = () => {
    Context.setLoadingOverlay(true);
    const {username, gender, dateOfBirth} = formik.values;

    const payload: updateUserPayloadType = {
      username, handedness: user?.handedness || '', allowTextNotifications: user?.allowTextNotifications || true,
      allowEmailNotifications: user?.allowEmailNotifications || true,
      allowPartnerPromos: user?.allowPartnerPromos || true, handicap: user?.handicap || '',
      firstname: user?.firstname || '', lastname: user?.lastname || '', gender,
      dateOfBirth: dateOfBirth ? dateOfBirth.format(dateFormat) : ''
    };

    ServiceClient.updateUser(payload).then(() => {
      finishRegistration();

    }).catch(err => {
      Context.setLoadingOverlay(false);
      console.log('updateUser error', err);

      if (err && err.message) {
        Context.setCancelModalConfig({
          visible: true, subtitle: err.message, onExit: onResume, title: 'Error', leftBtnText: 'OK'
        });
      }
    });
  };

  const phoneVerificationHandler = (code?: string) => {
    setPhoneVerificationDialog(false);

    if (code) {
      formik.setFieldValue('smsCode', code);
      setContactInfoStep(4);
      untouchedForm();
    }
  };

  const RegisterUser = () => {
    Context.setLoadingOverlay(true);

    Context.registerUser(formik.values).then(() => {
      Context.setLoadingOverlay(false);
      props.goNext();

    }).catch(err => {
      Context.setLoadingOverlay(false);
      console.log('registerUser error', err);

      if (err && err.message) {
        Context.setCancelModalConfig({
          visible: true, subtitle: err.message, onExit: onResume, title: 'Error', leftBtnText: 'OK'
        });
      }
    });
  };

  const untouchedForm = () => {
    formik.setTouched({phone: false, username: false, firstname: false, lastname: false});
  };

  const requestOtpCode = () => {
    Context.setLoadingOverlay(true);
    const val = formik.values.preferredOtpMethod === 1 ? {phone: formik.values.phone} : {email: formik.values.email};

    ServiceClient.requestOtpCode(val).then(() => {
      Context.setLoadingOverlay(false);
      setPhoneVerificationDialog(true);

    }).catch(err => {
      Context.setLoadingOverlay(false);
      console.log('requestOtpCode err', err);

      if (err && err.message) {
        Context.setCancelModalConfig({
          visible: true, subtitle: err.message, onExit: onResume, title: 'Error', leftBtnText: 'OK'
        });
      }
    });
  };

  const next = () => {
    formik.submitForm().then(async () => {
      const {errors, values} = formik;

      if (contactInfoStep === 1 && !errors.email) {
        setContactInfoStep(2);
        untouchedForm();

      } else if (contactInfoStep === 2) {
        if (formik.values.preferredOtpMethod === 1) setContactInfoStep(3);
        else requestOtpCode();
        untouchedForm();

      } else if (contactInfoStep === 3 && !errors.phone) {
        let isValid = false;

        try {
          isValid = phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(values.phone));

        } catch (error) {
          console.log('isValidNumber error', error);
        }

        if (isValid) {
          if (formik.values.smsCode) {
            setContactInfoStep(4);
            untouchedForm();

          } else {
            requestOtpCode();
          }

        } else {
          formik.setFieldError('phone', 'Invalid format');
        }
      }
    });
  };

  const onResume = () => {
    Context.setCancelModalConfig({...cancelModalConfig, visible: false});
  };

  const onCancel = () => {
    if (contactInfoStep === 1) props.onCancel();
    else setContactInfoStep(contactInfoStep - 1);
  };

  useEffect(() => {
    formik.setFieldValue('smsCode', '');
  }, [formik.values.phone]);

  useEffect(() => {
    (async () => {
      const params: any = route.params;

      if (params?.email) {
        await formik.setFieldValue('email', params.email);

      } else {
        // set form to dirty
        // we need this for showing an error message
        // if the player tries to go to the next page without changing any field
        await formik.setFieldValue('password', '1');
        await formik.setFieldValue('password', '');
      }
    })();
  }, []);

  return (
    <>
      {isFullSwing ? (
        <P fontFamily="DavisSans-Regular" mx={20} my={30} ta="center">
          We need a bit more information before you can start playing.
        </P>
      ) : (
        <P fw="500" fs={30} lh={24} ta="center" mb={59} mx={20}>Sign Up</P>
      )}

      <Div flex={1} mx={20}>
        {contactInfoStep === 1 ? (
          <Email formik={formik}/>
        ) : contactInfoStep === 2 ? (
          <>
            <P ta="center" fs={18} fw="500" mb={16}>{`When signing in, we will send\nyou a one-time password.`}</P>
            <P ta="center" fs={18} fw="700" mb={30}>{`Select which method you would\nlike to receive the password.`}</P>

            <Div mb={64} mx="auto" w={155}>
              <Pressable style={[MainStyle.row, MainStyle.a_i_c, MainStyle.m_b_13]}
                         onPress={() => formik.setFieldValue('preferredOtpMethod', 1)}>
                <MaterialIcons
                  name={`radio-button-${formik.values.preferredOtpMethod === 1 ? 'on' : 'off'}`}
                  size={18} color="white"
                />
                <P ml={15} fw="500" fs={18}>Text Message</P>
              </Pressable>

              <Pressable style={[MainStyle.row, MainStyle.a_i_c]}
                         onPress={() => formik.setFieldValue('preferredOtpMethod', 0)}>
                <MaterialIcons
                  name={`radio-button-${formik.values.preferredOtpMethod === 0 ? 'on' : 'off'}`}
                  size={18} color="white"
                />
                <P ml={15} fw="500" fs={18}>Email</P>
              </Pressable>
            </Div>

            <P opacity={0.5} ta="center" fw="500">
              {`Message and data rates may apply.\nWhen you opt in to the service, we will send you\na one-time passcode text message per login attempt.`}
            </P>
          </>
        ) : contactInfoStep === 3 ? (
          <Phone formik={formik}/>
        ) : contactInfoStep === 4 ? (
          <Name formik={formik}/>
        ) : null}
      </Div>

      {!isFullSwing && (
        <P fw="500" ta="center" my={46} mx={20}>
          <P fw="500" color={colors.newGreenText} onPress={() => navigation.navigate('TermsAndConditions')}>
            Terms and Conditions
          </P>
          {` and `}
          <P fw="500" color={colors.newGreenText} onPress={() => navigation.navigate('PrivacyPolicy')}>
            Privacy Policy
          </P>
        </P>
      )}

      {isFullSwing ? (
        <Btn mt={20} mb={30} bg={Object.keys(formik.errors).length > 0 ? "#BBBCBF" : "#3369FF"} rounded={4} onPress={next} mx={20}>
          <P lh={56} ta="center" fontFamily="DavisSans-Medium" color={Object.keys(formik.errors).length > 0 ? "#494A4C" : "white"}>Continue</P>
        </Btn>
      ) : (
        <Div mb={86} row mx={20}>
          <Button
            text="Back" bg="transparent" borderWidth={1} borderColor="white"
            onPress={onCancel} color="white" flex={1}
          />
          <Div w={10}/>
          <Button text="Next" flex={1} onPress={next}/>
        </Div>
      )}

      <OtpModal
        visible={phoneVerificationDialog} setVisible={phoneVerificationHandler}
        phone={formik.values.phone} email={formik.values.email} goBackBtn
        onSuccess={() => navigation.reset({index: 0, routes: [{name: 'challenges'}]})}
      />
    </>
  );
};

export default React.memo(ContactInfo);
